import axios from 'axios';
import axiosRetry from 'axios-retry';
import { handleApiErrorModal } from '../../components/ApiErrorModal';
import Cookies from 'js-cookie';
import { apiError } from '@/utils/apiError';
const statusArr = [403, 500];
const retryableErrorCodes = [
    'RequestTimeout',
    'InternalError',
    'CoreAPIUnavailable',
    'CloudIntegrationError'
];
export const api = axios.create({
    withCredentials: true,
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 60000 // 30-second default timeout
});
// Axios retry config
axiosRetry(api, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition(error) {
        const status = error.response?.status;
        const errorCodeValue = error.response?.data?.errors?.errorCode;
        return (status !== undefined &&
            statusArr.includes(status) &&
            retryableErrorCodes.includes(errorCodeValue));
    }
});
/** ------------------------------------------------
 * Timeout Retry with Exponential Backoff
 * If a request times out (>60s), retry up to
 * MAX_TIMEOUT_RETRIES times with an increasing
 * timeout. After all retries are exhausted an
 * error toast is shown to the user.
 * ------------------------------------------------ */
const INITIAL_TIMEOUT_MS = 60000;
const MAX_TIMEOUT_RETRIES = 5;
const TIMEOUT_BACKOFF_MULTIPLIER = 2; // timeout grows: 60→120→240→480→960s
const isTimeoutError = (error) => error?.code === 'ECONNABORTED' ||
    (error?.code === 'ERR_CANCELED' && /timeout/i.test(error?.message ?? ''));
const isNetworkError = (error) => !error?.response && !isTimeoutError(error) && error?.code !== 'ERR_CANCELED';
/** ------------------------------------------------
 * Transient Error Retry Interceptor (Timeout + Network)
 * Retries both timeout and network-level failures with
 * exponential backoff. Shows a toast only after all
 * retries are exhausted.
 *   Timeout:  up to MAX_TIMEOUT_RETRIES (5), timeout
 *             grows: 60→120→240→480→960s
 *   Network:  up to MAX_NETWORK_RETRIES (3), delay
 *             grows: 1→2→4s
 * ------------------------------------------------ */
const MAX_NETWORK_RETRIES = 3;
api.interceptors.response.use((response) => response, async (error) => {
    const config = error?.config;
    const timeout = isTimeoutError(error);
    const network = isNetworkError(error);
    if (!config || (!timeout && !network)) {
        return Promise.reject(error);
    }
    if (timeout) {
        config.__timeoutRetryCount = config.__timeoutRetryCount ?? 0;
        if (config.__timeoutRetryCount >= MAX_TIMEOUT_RETRIES) {
            config.skipErrorToast = true;
            handleApiErrorModal(true, {
                title: 'Request Timed Out',
                description: 'The request timed out after multiple attempts. Please try again later.'
            });
            return Promise.reject(error);
        }
        config.__timeoutRetryCount += 1;
        config.timeout = Math.round(INITIAL_TIMEOUT_MS *
            Math.pow(TIMEOUT_BACKOFF_MULTIPLIER, config.__timeoutRetryCount));
        console.warn(`[Retry] Timeout attempt ${config.__timeoutRetryCount}/${MAX_TIMEOUT_RETRIES} for ${config.url} — new timeout: ${config.timeout}ms`);
    }
    else {
        config.__networkRetryCount = config.__networkRetryCount ?? 0;
        if (config.__networkRetryCount >= MAX_NETWORK_RETRIES) {
            config.skipErrorToast = true;
            handleApiErrorModal(true, {
                title: 'Network Error',
                description: 'A network error occurred after multiple attempts. Please check your connection and try again.'
            });
            return Promise.reject(error);
        }
        config.__networkRetryCount += 1;
    }
    const retryCount = timeout
        ? config.__timeoutRetryCount
        : config.__networkRetryCount;
    const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 10000);
    await new Promise((resolve) => setTimeout(resolve, delay));
    return api(config);
});
// Add token to request headers
api.interceptors.request.use((config) => {
    const token = Cookies.get('access_token');
    if (token) {
        config.headers['Authorization'] = 'Bearer ' + token;
    }
    const uiTraceId = Cookies.get('ui_trace_id');
    if (uiTraceId) {
        config.headers['X-Request-ID'] = uiTraceId;
        config.headers['X-UI-Trace-ID'] = uiTraceId;
    }
    return config;
});
/** -------------------------------
 * Token Refresh with Request Queue
 ---------------------------------- */
let isRefreshing = false;
let failedQueue = [];
const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        }
        else {
            prom.resolve(token ?? '');
        }
    });
    failedQueue = [];
};
const refreshToken = async () => {
    const refreshToken = Cookies.get('refresh_token');
    if (!refreshToken)
        return null;
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/authentication/token/refresh`, {
        headers: { refreshtoken: refreshToken },
        withCredentials: true
    });
    return response?.data;
};
// Converts "oauth-unauthorized" or "InternalError" → "Oauth Unauthorized" / "Internal Error"
const formatErrorCode = (code) => code
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // "APIError" → "API Error"
    .replace(/([a-z\d])([A-Z])/g, '$1 $2') // "camelCase" → "camel Case"
    .replace(/[-_]/g, ' ') // hyphens/underscores → spaces
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
const extractRefId = (axiosError) => {
    const data = axiosError.response?.data;
    return (data?.errors?.errorLogRef ?? // { errors: { errorLogRef } }
        data?.errors?.requestLogRef ?? // legacy nested format
        data?.errorLogRef ?? // flat: { errorCode, description, errorLogRef }
        data?.log_ref ?? // { log_ref }
        data?.requestLogRef ?? // legacy top-level
        undefined);
};
const STATUS_TO_ERROR_CODE = {
    400: 'BAD_REQUEST',
    401: 'UNAUTHORIZED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    429: 'RATE_LIMITED',
    500: 'SERVER_ERROR',
    502: 'SERVER_ERROR',
    503: 'SERVICE_UNAVAILABLE',
    504: 'SERVER_ERROR'
};
const HTTP_STATUS_TITLES = {
    400: 'Bad Request',
    403: 'Access Denied',
    404: 'Not Found',
    408: 'Request Timed Out',
    409: 'Conflict',
    422: 'Validation Error',
    429: 'Too Many Requests',
    500: 'Server Error',
    502: 'Server Error',
    503: 'Service Unavailable',
    504: 'Server Error'
};
const extractErrorTitle = (axiosError) => {
    const data = axiosError.response?.data;
    const code = data?.errors?.errorCode ?? data?.errorCode ?? data?.error_code;
    if (code)
        return formatErrorCode(code);
    const status = axiosError.response?.status;
    if (status) {
        const statusTitle = HTTP_STATUS_TITLES[status];
        if (statusTitle)
            return statusTitle;
        const fallbackCode = STATUS_TO_ERROR_CODE[status];
        if (fallbackCode)
            return formatErrorCode(fallbackCode);
        return `HTTP ${status}`;
    }
    return 'Connection Error';
};
/** Extract a user-facing error message from an Axios-shaped error. */
const extractErrorMessage = (axiosError) => {
    const data = axiosError.response?.data;
    const status = axiosError.response?.status;
    const statusFallback = () => {
        const code = status ? STATUS_TO_ERROR_CODE[status] : undefined;
        return ((code ? apiError[code]?.message : undefined) ??
            (status ? HTTP_STATUS_TITLES[status] : undefined) ??
            'An error occurred');
    };
    if (!axiosError.response)
        return (apiError['NETWORK_ERROR']?.message ??
            'Unable to connect to the server. Please check your internet connection and try again.');
    if (!data)
        return statusFallback();
    // Format 2: { errors: { errorCode, description } }
    const errors = data.errors;
    if (errors) {
        const code = errors.errorCode;
        if (code && apiError[code]?.message)
            return apiError[code].message;
        if (errors.description)
            return errors.description;
    }
    // Format 3: flat { errorCode, description, errorLogRef }
    if (data.errorCode && apiError[data.errorCode]?.message)
        return apiError[data.errorCode].message;
    if (data.description)
        return data.description;
    // Format 1: { message, error_code, log_ref }
    if (data.message)
        return data.message;
    return statusFallback();
};
/** Build an enhanced Error object that carries backend metadata. */
const buildEnhancedError = (message, axiosError, statusCode, originalError) => {
    const enhanced = new Error(message);
    enhanced.backendResponse = axiosError.response?.data;
    enhanced.statusCode = statusCode;
    enhanced.originalError = originalError;
    return enhanced;
};
/** Clear all auth cookies/session and redirect to home. */
const clearSessionAndRedirect = () => {
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    Cookies.remove('user');
    Cookies.remove('ui_trace_id');
    sessionStorage.clear();
    const rawBasePath = process.env.REACT_APP_BASE_PATH || '/';
    const basePath = rawBasePath === '/' ? '/' : rawBasePath.replace(/\/+$/, '');
    // Normalize both sides to strip trailing slash before comparing so that
    // /cc/ and /cc are treated as the same location and we don't redirect in
    // an infinite loop.
    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    if (currentPath !== basePath) {
        window.location.href = basePath;
    }
};
const errorHandler = (error) => {
    const axiosError = error;
    const statusCode = axiosError.response?.status;
    // Timeout errors should not clear the session
    if (isTimeoutError(error)) {
        if (!axiosError.config?.skipErrorToast) {
            handleApiErrorModal(true, {
                title: 'Request Timed Out',
                description: 'The request timed out. Please try again later.'
            });
        }
        return Promise.reject(error);
    }
    // Only clear session on an explicit 401 — not on network/CORS/DNS errors
    if (statusCode === 401) {
        clearSessionAndRedirect();
        return Promise.reject(new Error('Authentication error'));
    }
    const errorMessage = extractErrorMessage(axiosError);
    if (!axiosError.config?.skipErrorToast) {
        handleApiErrorModal(true, {
            title: extractErrorTitle(axiosError),
            description: errorMessage,
            refId: extractRefId(axiosError)
        });
    }
    const enhancedError = buildEnhancedError(errorMessage, axiosError, statusCode, error);
    return Promise.reject(enhancedError);
};
/** Store tokens from a successful refresh response. */
const storeTokens = (accessToken, refreshTokenValue) => {
    Cookies.set('access_token', accessToken, {
        secure: true,
        sameSite: 'Strict'
    });
    Cookies.set('refresh_token', refreshTokenValue, {
        secure: true,
        sameSite: 'Strict'
    });
};
/** Retry the original request with a new access token. */
const retryWithNewToken = (originalRequest, accessToken) => {
    originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
    return api(originalRequest);
};
/** Handle token refresh when a 401 is received and we are the first caller. */
const handleRefreshAsInitiator = async (originalRequest, error) => {
    isRefreshing = true;
    originalRequest._retry = true;
    try {
        const result = await refreshToken();
        if (!result?.accessToken) {
            processQueue(new Error('Token refresh failed'), null);
            return errorHandler(error);
        }
        storeTokens(result.accessToken, result.refreshToken);
        processQueue(null, result.accessToken);
        return retryWithNewToken(originalRequest, result.accessToken);
    }
    catch (error_) {
        const refreshError = error_ instanceof Error ? error_ : new Error('Token refresh failed');
        processQueue(refreshError, null);
        return errorHandler(refreshError);
    }
    finally {
        isRefreshing = false;
    }
};
/** Enqueue the request to wait for an in-progress token refresh. */
const enqueueFailedRequest = (originalRequest) => {
    return new Promise(function (resolve, reject) {
        failedQueue.push({
            resolve: (token) => {
                resolve(retryWithNewToken(originalRequest, token));
            },
            reject: (err) => {
                errorHandler(err);
                reject(err);
            }
        });
    });
};
// Main response interceptor
api.interceptors.response.use((response) => response, async (error) => {
    const originalRequest = error.config;
    const isUnauthorized = error.response?.status === 401;
    const isAlreadyRetried = originalRequest?._retry;
    if (!isUnauthorized || !isAlreadyRetried || !originalRequest) {
        return errorHandler(error);
    }
    if (isRefreshing) {
        return enqueueFailedRequest(originalRequest);
    }
    return handleRefreshAsInitiator(originalRequest, error);
});
